import type { ArtistIntake, ConsentAcceptance } from "./schemas";
import { comparableAssessmentSuffix } from "./assessment";
import { MAX_RECEIPT_BYTES } from "./receiptSchema";
import { validateSignalCapsuleReceipt, type ShadowSignalCapsuleReceipt } from "./product";

export const RECEIPT_TRUST_STATEMENT = "Unsigned context: checksum integrity is not authenticated identity. It grants no analysis consent, rights, accounts, payments or publication.";
export const MAX_RECEIPT_TOKEN_CHARACTERS = Math.ceil(MAX_RECEIPT_BYTES / 3) * 4;
export type ReceiptImportResult =
  | { valid: true; receipt: ShadowSignalCapsuleReceipt; compatibility: "v1" | "legacy_v1_unknown_method" }
  | { valid: false; reason: string };

/** Check raw bytes before JSON parsing; object validation alone is too late. */
export function parseSignalReceipt(raw: unknown): ReceiptImportResult {
  if (typeof raw !== "string" || !raw.trim()) return { valid: false, reason: "invalid_json" };
  if (raw.length > MAX_RECEIPT_BYTES || new TextEncoder().encode(raw).byteLength > MAX_RECEIPT_BYTES) return { valid: false, reason: "receipt_resource_limit" };
  try {
    const parsed: unknown = JSON.parse(raw);
    const validation = validateSignalCapsuleReceipt(parsed);
    if (!validation.valid) return { valid: false, reason: validation.reason };
    const receipt = parsed as ShadowSignalCapsuleReceipt;
    return { valid: true, receipt, compatibility: receipt.capsule.assessment ? "v1" : "legacy_v1_unknown_method" };
  } catch {
    return { valid: false, reason: "invalid_json" };
  }
}

/** Supports UTF-8 shares and the historical btoa(JSON) Latin-1 transport. */
export function parseSharedReceiptToken(token: unknown): ReceiptImportResult {
  if (typeof token !== "string" || token.length > MAX_RECEIPT_TOKEN_CHARACTERS) return { valid: false, reason: "receipt_resource_limit" };
  if (!token || !/^[A-Za-z0-9+/]*={0,2}$/.test(token)) return { valid: false, reason: "invalid_share_token" };
  try {
    const binary = atob(token);
    if (binary.length > MAX_RECEIPT_BYTES) return { valid: false, reason: "receipt_resource_limit" };
    const bytes = Uint8Array.from(binary, character => character.charCodeAt(0));
    let utf8Result: ReceiptImportResult | undefined;
    try {
      utf8Result = parseSignalReceipt(new TextDecoder("utf-8", { fatal: true }).decode(bytes));
      if (utf8Result.valid) return utf8Result;
    } catch { /* Earlier Latin-1 shares can contain bytes invalid in UTF-8. */ }
    // Verify the original checksum after each decoding; never rewrite names to
    // make a transport interpretation fit. Even ambiguous Latin-1 remains intact.
    const legacyResult = parseSignalReceipt(binary);
    return legacyResult.valid ? legacyResult : utf8Result ?? legacyResult;
  } catch {
    return { valid: false, reason: "invalid_share_token" };
  }
}

export function encodeSharedReceiptToken(receipt: ShadowSignalCapsuleReceipt): string {
  const validation = validateSignalCapsuleReceipt(receipt);
  if (!validation.valid) throw new Error(validation.reason);
  const result = parseSignalReceipt(JSON.stringify(receipt));
  if (!result.valid) throw new Error(result.reason);
  const bytes = new TextEncoder().encode(JSON.stringify(result.receipt));
  let binary = "";
  for (let index = 0; index < bytes.length; index += 8192) binary += String.fromCharCode(...bytes.subarray(index, index + 8192));
  return btoa(binary);
}

export function importedReceiptMethod(receipt: ShadowSignalCapsuleReceipt): string {
  const assessment = receipt.capsule.assessment;
  if (!assessment || assessment.mode === "unknown") return "Method unknown — legacy or unspecified assessment";
  return `Declared method: ${assessment.mode.replaceAll("_", " ")} · ${assessment.rubricVersion}`;
}

/** Eligibility describes matching declared metadata, not authenticated identity. */
export function compareImportedReceipts(a: ShadowSignalCapsuleReceipt, b: ShadowSignalCapsuleReceipt): { delta: number | null; explanation: string } {
  if (!validateSignalCapsuleReceipt(a).valid || !validateSignalCapsuleReceipt(b).valid) return { delta: null, explanation: "No score delta: one receipt is invalid." };
  if (a.capsule.artistName !== b.capsule.artistName) return { delta: null, explanation: "No score delta: the declared artist names differ. Identity is not authenticated." };
  const modes = [a.capsule.assessment?.mode, b.capsule.assessment?.mode];
  if (modes.some(mode => !mode || ["demo", "unknown", "human_review"].includes(mode))) return { delta: null, explanation: "No score delta: the methods are unknown, illustrative, or an unauthenticated human-review claim." };
  if (comparableAssessmentSuffix([a.capsule, b.capsule], b.capsule.assessment).length !== 2) return { delta: null, explanation: "No score delta: assessment methods, rubrics, scopes or providers differ." };
  return { delta: b.capsule.readiness.score - a.capsule.readiness.score, explanation: "Matching declared artist name, method, rubric and provider. This difference describes preparation inputs; it does not measure musical improvement or authenticate either artifact." };
}

/** A continuity preview can seed a name, never consent or earlier artist inputs. */
export function freshIntakeFromReceipt(receipt: ShadowSignalCapsuleReceipt): { intake: ArtistIntake; consent: ConsentAcceptance } {
  const validation = validateSignalCapsuleReceipt(receipt);
  if (!validation.valid) throw new Error(validation.reason);
  return {
    intake: { artistName: receipt.capsule.artistName, email: "", genre: "", links: [], goals: [], aiComfortLevel: "analysis_only", critiqueMode: "direct", privacyLevel: "public_only", songText: "" },
    consent: { analysisConsent: false, rightsAttestation: false, aiDisclosureAcknowledged: false, marketingOptIn: false, benchmarkOptIn: false, version: "shadow-consent-v1" }
  };
}
